import CloseIcon from '@mui/icons-material/Close';
import { Alert, Box, Button, Divider, Fade, IconButton, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import LayoutContainer from '../components/LayoutWrapper';
import { removeFromCart, updateQuantity } from '../features/cartSlices';


const CartPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const cartItems = useSelector((state) => state.cart.items);
    const [coupon, setCoupon] = useState('');
    const [showCouponError, setShowCouponError] = useState(false);
    const [showUpdateMessage, setShowUpdateMessage] = useState(false);

    const total = cartItems.reduce(
        (sum, item) => sum + item.product.price * item.quantity,
        0
    );

    const handleDecrease = (item) => {
        if (item.quantity > 1) {
            dispatch(updateQuantity({ item, quantity: item.quantity - 1 }));
        }
    };

    const handleIncrease = (item) => {
        dispatch(updateQuantity({ item, quantity: item.quantity + 1 }));
    };
    const handleRemove = (item) => {
        dispatch(removeFromCart(item));
    };

    const handleUpdateCart = () => {
        setShowUpdateMessage(true);
        setTimeout(() => setShowUpdateMessage(false), 3000);
    };

    const handleApplyCoupon = () => {
        setShowCouponError(true);
        setTimeout(() => setShowCouponError(false), 3000);
    };

    return (
        <LayoutContainer sx={{ px: { xs: 2, md: 4 }, py: 5 }}>
            <Typography variant="h4" fontWeight={700} mb={3}>SHOPPING CART</Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
                {/* LEFT - CART ITEMS */}
                <Box sx={{ flex: 7, width: '100%' }}>
                    <Box>
                        <Box sx={{ display: { xs: 'none', md: 'flex' }, borderBottom: '1px solid #ddd', pb: 1 }}>
                            <Typography sx={{ flex: 1, fontWeight: 600 }}>PRODUCT</Typography>
                            <Typography sx={{ width: 120, fontWeight: 600 }}>QUANTITY</Typography>
                            <Typography sx={{ width: 100, fontWeight: 600 }}>SUBTOTAL</Typography>
                        </Box>

                        {cartItems.map((item, index) => (
                            <Box key={index} sx={{ py: 2, borderBottom: '1px solid #eee', width: '100%' }}>
                                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, width: '100%' }}>
                                    <Box component="img" src={item.product.image[0]} alt={item.product.name} sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1 }} />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography fontWeight={600} sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{item.product.name}</Typography>
                                        {item.color?.name && <Typography variant="body2">Type: {item.color.name || 'Default'} ($0.00)</Typography>}
                                        {item.color?.name && <Typography variant="body2">Size: {item.size} ($0.00)</Typography>}
                                        <Typography sx={{ color: 'crimson', fontWeight: 600, mt: 1 }}>${item.product.price.toFixed(2)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: { xs: 1, sm: 0 } }}>
                                        <Button onClick={() => handleDecrease(item)}>-</Button>
                                        <TextField value={item.quantity} size="small" sx={{ width: 40 }} inputProps={{ readOnly: true }} />
                                        <Button onClick={() => handleIncrease(item)}>+</Button>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: { xs: 1, sm: 0 } }}>
                                        <Typography fontWeight={600}>${(item.product.price * item.quantity).toFixed(2)}</Typography>
                                        <IconButton onClick={() => handleRemove(item)}><CloseIcon /></IconButton>
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: 2, mt: 3 }}>
                        <Button variant="outlined" fullWidth onClick={() => {
                            navigate('/shop');
                        }}>CONTINUE SHOPPING</Button>
                        <Button variant="contained" fullWidth onClick={handleUpdateCart}>UPDATE CART</Button>
                    </Box>

                    <Fade in={showUpdateMessage}>
                        <Alert severity="success" sx={{ mt: 2 }}>Cart has been updated.</Alert>
                    </Fade>
                </Box>

                {/* RIGHT - CART TOTAL */}
                <Box sx={{ flex: 5, width: '100%' }}>
                    <Box sx={{ border: '1px solid #ddd', p: { xs: 2, md: 3 }, borderRadius: 2, width: '100%' }}>
                        <Typography variant="h6" fontWeight={700} mb={2}>CART TOTALS</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography>Subtotal</Typography>
                            <Typography fontWeight={600}>${total.toFixed(2)}</Typography>
                        </Box>
                        <Box sx={{ mb: 1 }}>
                            <Typography>Shipping</Typography>
                            <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'normal', color: 'gray' }} >Enter your address to view shipping options. <br /><b>Calculate shipping</b></Typography>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography fontWeight={700}>Total</Typography>
                            <Typography fontWeight={700} color="crimson">${total.toFixed(2)}</Typography>
                        </Box>
                        <Button fullWidth variant="contained" sx={{ bgcolor: 'green', color: '#fff', fontWeight: 700 }} onClick={() => {
                            navigate('/checkout');
                        }}>
                            PROCEED TO CHECKOUT
                        </Button>

                        {/* Coupon */}
                        <Box mt={4}>
                            <Typography fontWeight={600} mb={1}>Coupon</Typography>
                            <TextField
                                fullWidth
                                placeholder="Coupon code"
                                size="small"
                                value={coupon}
                                onChange={(e) => setCoupon(e.target.value)}
                            />
                            <Button variant="outlined" fullWidth sx={{ mt: 1 }} onClick={handleApplyCoupon}>
                                Apply coupon
                            </Button>
                            <Fade in={showCouponError}>
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    Coupon does not exist or has expired!
                                </Alert>
                            </Fade>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </LayoutContainer>
    );
};

export default CartPage;